import React, { useEffect, useState } from 'react'
import PageHeader from '../../PageHeader/PageHeader'
import { useDispatch, useSelector } from 'react-redux';
import { setApiErrorJson, setApiJson } from '../../redux/Actions/ApiAction';
import { hasError } from '../../utils/utils';
import { DollarSign, Gift, Percent, Users } from 'lucide-react';
import AppInput from '../../components/AppInput/AppInput';
import TextArea from '../../components/AppInput/TextArea';
import { duration } from '../../constant/Options';
import AppSelect from '../../components/AppSelect/AppSelect';
import { getSelectedUnit } from '../../storage/Storage';
import { HitApi } from '../../Api/ApiHit';
import { addMembership, searchMembership, updateMembership, searchSalonChild } from '../../constant/Constant';
import toast from 'react-hot-toast';
import AppButton from '../../components/AppButton/AppButton';
import { MembershipValidation } from '../../validationscheema/MembershipValidation';
import MultiSelect from '../../components/AppSelect/MultiSelect';
import { useNavigate } from 'react-router-dom';

function AddMembship() {
    const [isEditMode, setIsEditMode] = useState(false);
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Get ID from URL
    const url = window.location.pathname;
    const urlId = url.split('/')[3];

    useEffect(() => {
        fetchService();

        if (urlId) {
            setIsEditMode(true);
            loadMembershipData();
        } else {
            // Initialize with empty data for add mode
            const initialData = {
                name: "",
                description: "",
                price: "",
                duration_value: "",
                duration_unit: "",
                membershipType: "value_added",
                valueAddedAmount: "",
                fixDiscountPercentage: "",
                excludedServices: []
            };
            dispatch(setApiJson(initialData));
            dispatch(setApiErrorJson({}));
        }
    }, [urlId]);


    const handleDurationChange = (field, value) => {
        dispatch(setApiJson({
            ...ApiReducer.apiJson,
            duration: {
                ...ApiReducer.apiJson.duration,
                [field]: value
            }
        }));
    };

    const loadMembershipData = async () => {
        try {
            setLoading(true);
            const json = {
                page: 1,
                limit: 10,
                search: {
                    _id: urlId
                }
            };

            const res = await HitApi(json, searchMembership);

            if (res?.statusCode === 200) {
                const membershipData = res?.data?.docs?.[0];

                if (membershipData) {
                    dispatch(setApiJson(membershipData));
                    dispatch(setApiErrorJson({}));
                } else {
                    toast.error('Membership not found');
                    navigate('/membership');
                }
            } else {
                toast.error('Failed to load membership data');
            }
        } catch (error) {
            console.error("Error loading membership:", error);
            toast.error('Error loading membership data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchService = async () => {
        try {
            const json = {
                page: 1,
                limit: 20,
                search: {
                    unitIds: getSelectedUnit()?._id
                }
            };

            const res = await HitApi(json, searchSalonChild);

            if (res?.statusCode === 200) {
                const options = res.data.docs?.map(service => ({
                    value: service._id,
                    label: service.name,
                })) || [];
                setServiceOptions(options);
            }
        } catch (error) {
            console.error("Error fetching services:", error);
            toast.error('Failed to load service options');
        }
    };

    const handleMembershipTypeChange = (value) => {
        const updatedData = {
            ...ApiReducer.apiJson,
            membershipType: value
        };

        // Clear type-specific fields when switching types
        if (value === 'value_added') {
            updatedData.fixDiscountPercentage = "";
        } else if (value === 'fix_discount') {
            updatedData.valueAddedAmount = "";
        }

        dispatch(setApiJson(updatedData));
    };

    const handleSubmit = async () => {
        setSubmitLoading(true);

        try {
            const errors = await MembershipValidation(ApiReducer?.apiJson);
            console.log("errors", errors);

            dispatch(setApiErrorJson(errors));

            if (Object.keys(errors).length === 0) {
                const membershipData = {
                    ...ApiReducer.apiJson,
                    unitIds: getSelectedUnit()?._id,
                    isActive: true,
                };

                const endpoint = isEditMode ? updateMembership : addMembership;
                const res = await HitApi(membershipData, endpoint);

                const successMessage = isEditMode
                    ? "Membership updated successfully"
                    : "Membership created successfully";

                if (res.statusCode === 200 || res.statusCode === 201) {
                    toast.success(successMessage + '!');
                    setTimeout(() => {
                        navigate('/membership');
                    }, 1000);
                } else {
                    toast.error(res?.message || `Failed to ${isEditMode ? 'update' : 'create'} membership. Please try again.`);
                }
            }
        } catch (error) {
            console.error('Error submitting membership:', error);
            toast.error(`Error ${isEditMode ? 'updating' : 'creating'} membership. Please try again.`);
        } finally {
            setSubmitLoading(false);
        }
    };

    // Show loading state while fetching data in edit mode
    if (loading && isEditMode) {
        return (
            <div className='p-5'>
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg">Loading membership data...</div>
                </div>
            </div>
        );
    }

    return (
        <div className='p-5'>
            <PageHeader
                title={isEditMode ? 'Edit Membership' : 'Add Membership'}
                description={isEditMode ? 'Update membership plan configuration' : 'Create and configure new membership plan'}
            />
            <div className="bg-white p-8 border rounded-xl mb-6">
                <div className="text-lg font-medium mb-5 flex justify-between items-center">
                    <span>Membership Information</span>
                </div>

                <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Membership Type</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className="flex items-center cursor-pointer p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                            <input
                                type="radio"
                                name="membershipType"
                                value="value_added"
                                checked={ApiReducer?.apiJson?.membershipType === 'value_added'}
                                onChange={(e) => handleMembershipTypeChange(e.target.value)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                disabled={submitLoading}
                            />
                            <div className="ml-3">
                                <div className="font-medium text-gray-900">Value Added</div>
                                <div className="text-sm text-gray-500">Pay amount, get more in wallet</div>
                            </div>
                        </label>
                        <label className="flex items-center cursor-pointer p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                            <input
                                type="radio"
                                name="membershipType"
                                value="fix_discount"
                                checked={ApiReducer?.apiJson?.membershipType === 'fix_discount'}
                                onChange={(e) => handleMembershipTypeChange(e.target.value)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                disabled={submitLoading}
                            />
                            <div className="ml-3">
                                <div className="font-medium text-gray-900">Fix Discount</div>
                                <div className="text-sm text-gray-500">Fixed % discount on all services</div>
                            </div>
                        </label>
                    </div>
                </div>

                <div className="mb-6">
                    <AppInput
                        title="Membership Name"
                        placeholder="Enter membership name"
                        name="name"
                        important={true}
                        error={hasError('name', ApiReducer)}
                        icon={<Users size={18} />}
                        disabled={submitLoading}
                    />
                </div>
                <div className="mb-6">
                    <TextArea
                        title="Membership Description"
                        placeholder="Enter membership description"
                        name="description"
                        error={hasError('description', ApiReducer)}
                        rows={4}
                        disabled={submitLoading}
                    />
                </div>
                <div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <AppInput
                            title="Membership Price"
                            placeholder="Enter membership price"
                            name="price"
                            important={true}
                            error={hasError('price', ApiReducer)}
                            icon={<DollarSign size={18} />}
                            type="number"
                            step="0.01"
                            prefix="₹"
                            disabled={submitLoading}
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <AppInput
                                title="Duration Value"
                                placeholder="Duration"
                                name="duration_value"
                                important={true}
                                value={ApiReducer.apiJson?.duration?.value || ""}
                                onChange={(e) => handleDurationChange('value', e.target.value)}
                                error={hasError('duration_value', ApiReducer)}
                                type="number"
                                min="1"
                                disabled={submitLoading}
                            />
                            <AppSelect
                                title="Duration Unit"
                                placeholder="Unit"
                                name="duration_unit"
                                value={ApiReducer.apiJson?.duration?.unit || ""}
                                onChange={(value) => handleDurationChange('unit', value)}
                                options={duration}
                                error={hasError('duration_unit', ApiReducer)}
                                disabled={submitLoading}
                            />
                        </div>
                    </div>

                    {/* Type-specific fields */}
                    {ApiReducer?.apiJson?.membershipType === 'value_added' && (
                        <div className="mb-6">
                            <AppInput
                                title="Value Added Amount"
                                placeholder="Amount credited to wallet"
                                name="valueAddedAmount"
                                important={true}
                                error={hasError('valueAddedAmount', ApiReducer)}
                                icon={<Gift size={18} />}
                                type="number"
                                step="0.01"
                                prefix="₹"
                                disabled={submitLoading}
                            />
                        </div>
                    )}

                    {ApiReducer?.apiJson?.membershipType === 'fix_discount' && (
                        <div className="mb-6">
                            <AppInput
                                title="Fix Discount Percentage"
                                placeholder="Discount percentage for all services"
                                name="fixDiscountPercentage"
                                important={true}
                                error={hasError('fixDiscountPercentage', ApiReducer)}
                                icon={<Percent size={18} />}
                                type="number"
                                step="0.01"
                                suffix="%"
                                min="0"
                                max="100"
                                disabled={submitLoading}
                            />
                        </div>
                    )}

                    {/* Excluded Services for value_added and fix_discount types */}
                    {(ApiReducer?.apiJson?.membershipType === 'value_added' || ApiReducer?.apiJson?.membershipType === 'fix_discount') && (
                        <div className="mb-6">
                            <MultiSelect
                                title="Excluded Services (Optional)"
                                name="excludedServices"
                                options={serviceOptions}
                                placeholder="Select services to exclude from this membership"
                                error={hasError('excludedServices', ApiReducer)}
                                disabled={submitLoading}
                            />
                        </div>
                    )}

                    <div className='mt-5 flex justify-end'>
                        <AppButton
                            buttontext={submitLoading ? "Processing..." : (isEditMode ? "Update Membership" : "Add Membership")}
                            onClick={handleSubmit}
                            disabled={submitLoading}
                            isLoading={submitLoading}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AddMembship